import React from "react";

/**
 * StatusConnector Component
 * 
 * Renders an SVG line connecting the scheduled task card (left) to the handyman requests container (right).
 * The line color and style change based on the task status.
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - Current task status (e.g., 'pending_offers', 'scheduled', 'completed')
 * @param {boolean} props.animated - Whether to show animated pulse effect
 * @returns {JSX.Element} SVG connector line
 */
const StatusConnector = ({ status = "pending_offers", animated = false }) => {
    // Define color schemes for different statuses
    const statusColors = {
        pending_offers: {
            stroke: "#F59E0B", // Yellow/Orange - waiting for offers
            fill: "#FEF3C7",
            label: "Pending Offers",
        },
        searching: {
            stroke: "#3B82F6", // Blue - actively searching
            fill: "#DBEAFE",
            label: "Searching",
        },
        handyman_selected: {
            stroke: "#6366F1", // Indigo - handyman selected
            fill: "#E0E7FF",
            label: "Handyman Selected",
        },
        scheduled: {
            stroke: "#10B981", // Green - confirmed and scheduled
            fill: "#D1FAE5",
            label: "Scheduled",
        },
        in_progress: {
            stroke: "#8B5CF6", // Purple - work in progress
            fill: "#EDE9FE",
            label: "In Progress",
        },
        approved: {
            stroke: "#059669", // Emerald - approved
            fill: "#D1FAE5",
            label: "Approved",
        },
        completed: {
            stroke: "#047857", // Dark Green - completed
            fill: "#A7F3D0",
            label: "Completed",
        },
        cancelled: {
            stroke: "#EF4444", // Red - cancelled
            fill: "#FEE2E2",
            label: "Cancelled",
        },
        disputed: {
            stroke: "#F97316", // Orange - disputed
            fill: "#FFEDD5",
            label: "Disputed",
        },
        payment_received: {
            stroke: "#059669", // Emerald - payment received
            fill: "#D1FAE5",
            label: "Payment Received",
        },
        expired: {
            stroke: "#9CA3AF", // Gray - expired
            fill: "#F3F4F6",
            label: "Expired",
        },
        on_the_way: {
            stroke: "#3B82F6", // Blue - handyman on the way
            fill: "#DBEAFE",
            label: "On The Way",
        },
        arrived: {
            stroke: "#8B5CF6", // Purple - handyman arrived
            fill: "#EDE9FE",
            label: "Arrived",
        },
        work_started: {
            stroke: "#8B5CF6", // Purple - work started
            fill: "#EDE9FE",
            label: "Work Started",
        },
    };

    // Get color configuration for current status, fallback to gray if status not found
    const colorConfig = statusColors[status] || {
        stroke: "#9CA3AF",
        fill: "#F3F4F6",
        label: "Unknown",
    };

    return (
        <div className="flex items-center justify-center px-4">
            {/* SVG Container */}
            <svg
                width="100%"
                height="60"
                className="overflow-visible"
                style={{ minWidth: "80px", maxWidth: "200px" }}
            >
                {/* Dashed line connecting left card to right container */}
                <line
                    x1="0"
                    y1="30"
                    x2="100%"
                    y2="30"
                    stroke={colorConfig.stroke}
                    strokeWidth="3"
                    strokeDasharray="8 4"
                    className={animated ? "animate-pulse" : ""}
                />

                {/* Circle at the start (left side - task card) */}
                <circle
                    cx="0"
                    cy="30"
                    r="8"
                    fill={colorConfig.stroke}
                    className={animated ? "animate-pulse" : ""}
                />

                {/* Circle at the end (right side - requests container) */}
                <circle
                    cx="100%"
                    cy="30"
                    r="8"
                    fill={colorConfig.stroke}
                    className={animated ? "animate-pulse" : ""}
                />

                {/* Status badge in the middle */}
                <g transform="translate(50%, 30)">
                    {/* Background rectangle for status label */}
                    <rect
                        x="-50"
                        y="-12"
                        width="100"
                        height="24"
                        rx="12"
                        fill={colorConfig.fill}
                        stroke={colorConfig.stroke}
                        strokeWidth="2"
                    />

                    {/* Status text */}
                    <text
                        x="0"
                        y="4"
                        textAnchor="middle"
                        className="text-xs font-semibold"
                        fill={colorConfig.stroke}
                        style={{ fontSize: "11px" }}
                    >
                        {colorConfig.label}
                    </text>
                </g>
            </svg>
        </div>
    );
};

export default StatusConnector;
